import type {
  LegalDocType,
  LegalVersionWithId,
  UserLegalAcceptance,
} from '@/shared/schema';
import {
  addDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import {
  legalVersionsCol,
  userLegalAcceptancesCol,
} from './helpers/firestore-refs';

export const LegalService = {
  /**
   * Fetches the latest version of a specific legal document type.
   */
  getLatestVersion: async (
    docType: LegalDocType,
  ): Promise<LegalVersionWithId | null> => {
    const q = query(
      legalVersionsCol(),
      where('docType', '==', docType),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return snap.docs[0].data() as LegalVersionWithId;
  },

  /**
   * Checks if a user has already accepted a specific version of a document.
   */
  checkUserAcceptance: async (
    userId: string,
    versionId: string,
  ): Promise<boolean> => {
    const q = query(
      userLegalAcceptancesCol(),
      where('userId', '==', userId),
      where('versionId', '==', versionId),
      limit(1),
    );
    const snap = await getDocs(q);
    return !snap.empty;
  },

  /**
   * Records a user's acceptance of a legal document.
   */
  recordAcceptance: async (params: {
    userId: string;
    docType: LegalDocType;
    versionId: string;
    versionTag: string;
    ipAddress: string;
    userAgent: string;
    metadata?: Record<string, any>;
  }): Promise<void> => {
    const acceptance: Omit<UserLegalAcceptance, 'id'> = {
      userId: params.userId,
      docType: params.docType,
      versionId: params.versionId,
      versionTag: params.versionTag,
      acceptedAt: serverTimestamp(),
      ipAddress: params.ipAddress,
      userAgent: params.userAgent,
    };
    // Only add metadata if provided
    if (params.metadata) {
      acceptance.metadata = params.metadata;
    }
    await addDoc(userLegalAcceptancesCol(), acceptance);
  },

  /**
   * Utility to get user's IP address using a public API.
   */
  getIpAddress: async (): Promise<string> => {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      return data.ip || '0.0.0.0';
    } catch (e) {
      console.error('Failed to get IP address:', e);
      return '0.0.0.0';
    }
  },
};
